/* eslint-disable @typescript-eslint/no-floating-promises */
import InfoIcon from "@mui/icons-material/Info";
import { Button, CircularProgress } from "@mui/material";
import { Box } from "@mui/system";
import { usePopup } from "@nodeon/react-components";
import { Map } from "ol";
import React from "react";
import { useTranslation } from "react-i18next";
import { useSeaSurveyApi } from "~/api/GeneratedApi";
import SeaSurveyDialog from "~/pages/marine-survey-and-design-page/dialogs/SeaSurveyDialog";
import { setCesiumPreloadMeas, setMapType } from "~/redux/reducers/mapReducer";
import { useAppDispatch, useAppSelector } from "~/redux/store/store";
import { useCustomQuery } from "~/utils/hooks/useCustomQuery";
import { SeaSurveyMapItem } from "../types/mapItems";
import { GenericPopup } from "./GenericPopup";

type Props = {
  map: Map;
} & (ClickProps | HoverProps);

type ClickProps = {
  clickData: SeaSurveyMapItem;
  hoverData?: SeaSurveyMapItem;
};

type HoverProps = {
  hoverData: SeaSurveyMapItem;
  clickData?: SeaSurveyMapItem;
};

export const MeasurementPopup = ({
  hoverData,
  clickData,
  map,
}: Props): JSX.Element | null => {
  const dispatch = useAppDispatch();
  const state = useAppSelector((x) => x.map.cesiumState);
  const { t } = useTranslation("cesium");
  const { getSeaSurvey } = useSeaSurveyApi();
  const { show } = usePopup();
  const [seaSurveyToViewId, setSeaSurveyToViewId] = React.useState<number>();

  const data = clickData ?? hoverData;

  useCustomQuery(
    { key: "sea-survey", id: seaSurveyToViewId },
    () => getSeaSurvey(seaSurveyToViewId!),
    {
      enabled: !!seaSurveyToViewId,
      refetchOnWindowFocus: false,
      onSuccess: (d) => {
        show(
          ({ close }) => (
            <SeaSurveyDialog
              onClose={() => {
                close();
                setSeaSurveyToViewId(undefined);
              }}
              onAdd={() => {
                close();
                setSeaSurveyToViewId(undefined);
                return null;
              }}
              onEdit={() => {
                close();
                setSeaSurveyToViewId(undefined);
                return null;
              }}
              activeAction="view"
              seaSurvey={d}
            />
          ),
          { noDialog: true },
        );
      },
    },
  );

  if (!data) return null;

  const { name, description } = data;

  return (
    <GenericPopup
      map={map}
      clicked={!!map}
      avatarText={name[0]}
      title={name}
      subheader={description ?? ""}
      content={
        <Box sx={{ display: "flex" }}>
          <Button
            variant="contained"
            disabled={state === "loading"}
            color="secondary"
            onClick={() => {
              if (!data.files?.[0]) return;
              dispatch(setMapType("3d"));
              dispatch(
                setCesiumPreloadMeas({
                  measId: data.id,
                  mapId: data.mapId,
                }),
              );
            }}
            startIcon={state === "loading" && <CircularProgress size={18} />}
          >
            {t("view-in-3d")}
          </Button>
          <Button
            sx={{ ml: "auto" }}
            variant="contained"
            color="secondary"
            startIcon={<InfoIcon />}
            onClick={() => setSeaSurveyToViewId(data.id)}
          >
            {t("general:information")}
          </Button>
        </Box>
      }
    />
  );
};
